<template>
  <v-card flat class="mb-7 w-100">
    <v-card-text class="pa-5">
      <div class="d-md-flex align-start">
        <div class="d-flex flex-column ml-3">
          <h3 class="title font-weight-regular">Detalle del código</h3>
          <h5>Matriculados con este código</h5>
        </div>
        <v-spacer></v-spacer>

        <v-btn color="secondary" class="mt-3 mt-md-0" :loading="exportLoading" @click="exportCsv">
          <v-icon left>mdi-download</v-icon> Exportar CSV
        </v-btn>
      </div>

      <div class="d-flex align-center flex-wrap pa-3 pt-4">
        <div class="mr-4" style="max-width: 420px;">
          <v-text-field
            v-model="filters.search"
            prepend-inner-icon="mdi-magnify"
            dense outlined clearable hide-details
            label="Buscar alumno (nombre, email, DNI)"
            @input="debouncedFetch"
          />
        </div>

        <div class="mr-4" style="max-width: 220px;">
          <v-select
            v-model="filters.estadoComision"
            :items="estadoOptions"
            item-text="text" item-value="value"
            label="Comisión" dense outlined clearable hide-details
            @change="onFilterChanged"
          />
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="paginatedRows"
        :loading="loading"
        :page.sync="filters.page"
        :items-per-page="pageSize"
        hide-default-footer
      >
        <template v-slot:item.usuario="{ item }">
          <div class="d-flex flex-column">
            <span class="font-weight-medium text-no-wrap">{{ item.usuario }}</span>
            <small class="blue-grey--text text--lighten-1">{{ item.email }}</small>
          </div>
        </template>

        <template v-slot:item.comision_pagada="{ item }">
          <v-btn
            small
            :loading="isToggling(item.id)"
            :color="item.comision_pagada ? 'green' : 'orange'"
            dark
            @click="toggleComision(item)"
          >
            {{ item.comision_pagada ? 'Pagado' : 'Pendiente' }}
          </v-btn>
        </template>

        <template v-slot:footer>
          <div class="pagination d-flex justify-space-between ml-auto mr-0 mt-5 w-100">
            <p class="ma-auto">{{ filters.page }} de {{ totalPages }}</p>
            <div>
              <v-btn icon @click="prevPage" :disabled="filters.page === 1"><v-icon>mdi-chevron-left</v-icon></v-btn>
              <v-btn icon @click="nextPage" :disabled="filters.page >= totalPages"><v-icon>mdi-chevron-right</v-icon></v-btn>
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from 'axios'
import { downloadCsv } from '../../utils/common'

const BASE_URL = '/api/admin/matriculas/codigos_referencia'

const UPDATE_COMISION_URL = (matriculaId) =>
  `/api/admin/matriculas/matriculas/${matriculaId}/update_comision_pagada/`
export default {
  name: 'TheCodeDetail',
  data: () => ({
    loading: false,
    exportLoading: false,
    rows: [],                 
    pageSize: 20,
    filters: {
      page: 1,
      search: '',
      estadoComision: '',   
    },
    headers: [
      { text: 'Alumno',           value: 'usuario',             sortable: false, align: 'start' },
      { text: 'Sección',          value: 'seccion',             sortable: false, align: 'start' },
      { text: 'Fecha matrícula',  value: 'fecha_matricula',     sortable: false, align: 'center' },
      { text: 'Monto pagado',     value: 'monto_pagado',        sortable: false, align: 'end' },
      { text: '% Comisión',       value: 'porcentaje_comision', sortable: false, align: 'end' },
      { text: 'Monto comisión',   value: 'monto_comision',      sortable: false, align: 'end' },
      { text: 'Estado comisión',  value: 'comision_pagada',     sortable: false, align: 'center' },
    ],
    estadoOptions: [
      { text: 'Todos',     value: '' },
      { text: 'Pagado',    value: 'pagado' },
      { text: 'Pendiente', value: 'pendiente' },
    ],
    _t: null,
    toggling: {}, 
  }),
  computed: {
    codeId () {
      return this.$route.params.id
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.rows.length / this.pageSize))
    },
    paginatedRows () {
      const start = (this.filters.page - 1) * this.pageSize
      const end   = start + this.pageSize
      return this.rows.slice(start, end)
    }
  },
  methods: {
    onFilterChanged () {
      this.filters.page = 1
      this.fetch()
    },
    debouncedFetch () {
      clearTimeout(this._t)
      this._t = setTimeout(() => {
        this.filters.page = 1
        this.fetch()
      }, 400)
    },
    async fetch () {
      try {
        this.loading = true
        const params = new URLSearchParams({
          search: this.filters.search || '',
          estado_comision: this.filters.estadoComision || '',
        })
        const { data } = await axios.get(`${BASE_URL}/${this.codeId}/matriculas/?${params}`)
        this.rows = Array.isArray(data) ? data : (data.results || [])
        this.filters.page = 1
      } finally {
        this.loading = false
      }
    },

    isToggling (id) {
      return !!this.toggling[id]
    },
    async toggleComision (item) {
      if (!item || !item.id) return
      const nuevoEstado = !item.comision_pagada
      try {
        this.$set(this.toggling, item.id, true)
        await axios.post(UPDATE_COMISION_URL(item.id), { comision_pagada: nuevoEstado })
        item.comision_pagada = nuevoEstado
        if (this.filters.estadoComision) {
          await this.fetch()
        }
      } catch (e) {
        console.error(e)
        item.comision_pagada = !nuevoEstado
        await this.$root.$confirm({
          title: '¡Lo sentimos!',
          message: 'Ha ocurrido un error actualizando su código. Inténtelo nuevamente en unos minutos.',
          acceptText: 'Aceptar'
        })
      } finally {
        this.$delete(this.toggling, item.id)
      }
    },

    async exportCsv () {
      try {
        this.exportLoading = true
        const params = new URLSearchParams({
          search: this.filters.search || '',
          estado_comision: this.filters.estadoComision || '',
        })
        const res = await axios.get(`${BASE_URL}/${this.codeId}/export_csv/?${params}`, { responseType: 'blob' })
        const csvText = res.data instanceof Blob ? await res.data.text() : res.data
        const cd = res.headers && res.headers['content-disposition']
        const filenameMatch = cd && /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(cd)
        const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : `codigo_${this.codeId}_matriculas.csv`
        downloadCsv(csvText, filename)
      } catch (e) {
        console.error(e)
        await this.$root.$confirm({
          title: '¡Lo sentimos!',
          message: 'No fue posible exportar el CSV. Inténtelo nuevamente en unos minutos.',
          acceptText: 'Aceptar'
        })
      } finally {
        this.exportLoading = false
      }
    },
    nextPage () {
      if (this.filters.page < this.totalPages) this.filters.page++
    },
    prevPage () {
      if (this.filters.page > 1) this.filters.page--
    },
  },
  async created () {
    await this.fetch()
  }
}
</script>